/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, ComponentFactoryResolver, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import {ReportService} from '../../../../services/report.service';
import {
  ChecklistProvider,
  ClientProvider,
  ClientUserProvider,
  ReportEntityProvider,
  ReportProvider,
  UserProvider,
  TagProvider,
  DocumentProvider
} from '../../../../providers';
import {
  ClientUser,
  ProfileInfoType,
  PurchaseAgreementType,
  Report,
  ReportClientType,
  ReportClientUser,
  ReportUser,
  ReportUserType,
  Stage,
  StandardOfValue,
  User,
  Helpers,
  HistoryStage,
  Document
} from '../../../../model';
import {InputType} from '../../../../model/enums/input.type';
import {CreateNewTypes} from '../../../../model/enums/create.type';
import {SearchableItem} from '../../../../model/interface/searchable.item';
import {SearchableSelection} from '../../../../model/interface/searchable.selection';
import {PresetValue} from '../../../../model/preset-value';
import {StageService, UserService, ModeService,LogService} from '../../../../services';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {ConfirmationDialogComponent} from '../../../fields';
import {ReportValuationType, ReportType, ReportDelay, ReportRecalculatedResult, DocumentState} from '../../../../model/report';
import {CorporateEntity} from 'src/app/model/corporate.entity';
import {ReportPageInterface} from '../../../../model/interface/report.page';
import {CheckListEntityDetails} from '../../../../model/checklist';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { Router } from '@angular/router';
import { Tag } from 'src/app/model/tag';
import { ClipboardService } from 'ngx-clipboard';
import { DueDateComponent } from '../../report/components';
import { ReportStageModal, ConfirmAssigneeModal, AssignCollectorModal, EngageReportFileModal, EditReportDelayModal, ChangeAssigneeModal, EngageReportManualModal, EngageReportModal, RequestInfoModal, ReportFollowUpModal, ReportLinkComponent, ChangeResearchedSpreadedModal, SiteVisitManualModal } from '../../report/modals';
import * as FileSaver from 'file-saver';
import { SystemType } from 'src/app/model/enums/system.type';
import { CreateNewClientUserModal } from 'src/app/components/modal';import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import { ReportFollowUp } from 'src/app/model/report.followup';
import { ReportLink } from 'src/app/model/report-link';
import { DocumentType } from 'src/app/model/enums/document.type';
Quill.register('modules/blotFormatter', BlotFormatter);
declare var moment: any;

@Component({
  selector: 'page-report-ea-general',
  templateUrl: 'report-ea.general.page.html',
  styleUrls: ['report-ea.general.page.scss'],
  styles: [
    `
      :host > .tooltip-inner {
        width: 500px;
        max-width: 500px;
        color: black;
        background-color: #ffffff;
        transition: all 200ms ease;
        border: 1px solid #d4d4d4;
        text-align: left;
        border-radius: 0;
        font-size: 14px;
      }
      :host > .tooltip.top .tooltip-arrow:before,
      :host > .tooltip.top .tooltip-arrow {
        border-top-color: #d4d4d4;
      }
    `
  ],
})
export class ReportEaGeneralPage implements OnInit, OnDestroy, ReportPageInterface {
  @ViewChild('dueDate') dueDateField: DueDateComponent;
  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;

  public tooltip: ComponentRef <any>;
  isSiteVisitModalOpen = false;
  isEngageModalOpen = false;

  currentReport: Report;
  private reportSub: any;
  private reportLockSub: any;
  userSub: any;
  currentUser: User;
  RSState = DocumentState;

  stagesList: Stage[];
  private stagesListSub: any;

  stages: Stage[];
  private stagesSub: any;

  stagesCompact = true;

  isCollapsed: CollapsedInfo = CollapsedInfo.createByRole();
  typeOfInput = InputType;
  typesOfData = CreateNewTypes;
  ReportClientType = ReportClientType;
  ProfileInfoType = ProfileInfoType;
  ReportUserType = ReportUserType;

  isLoadingPreview = false;
  preview: BsModalRef;
  fileEngageModal: BsModalRef;
  requestInfoModal: BsModalRef;
  manualEngageModal: BsModalRef;
  editModal: BsModalRef;
  isLocked: boolean;
  DocumentType = DocumentType;

  purposeValues: SearchableItem[] = [
    PresetValue.create('SBA Collateralization', 'SBA Collateralization'),
    PresetValue.create('SBA Liquidation', 'SBA Liquidation'),
    PresetValue.create('Non-SBA Liquidation', 'Non-SBA Liquidation'),
    PresetValue.create('Buy/Sell (Non-Bank)', 'Buy/Sell (Non-Bank)'),
    PresetValue.create('Other', 'Other'),
  ];

  valTypeValues: SearchableItem[] = [
    PresetValue.create('Desktop', ReportValuationType.DESKTOP),
    PresetValue.create('Summary', ReportValuationType.SUMMARY),
    PresetValue.create('Both (Desktop and Summary)', ReportValuationType.BOTH)
  ];

  sovValues: SearchableItem[] = [
    PresetValue.create('Fair Market Value', StandardOfValue.FAIR_MARKET_VALUE),
    PresetValue.create('Orderly Liquidation Value', StandardOfValue.ORDERLY_LIQUIDATION_VALUE),
    PresetValue.create('Forced Liquidation Value', StandardOfValue.FORCED_LIQUIDATION_VALUE),
    PresetValue.create('Fair Market Value - Installed', StandardOfValue.FAIR_MARKET_VALUE_INSTALLED),
    PresetValue.create('Liquidation Value in Place', StandardOfValue.LIQUIDATION_VALUE_IN_PLACE),
    PresetValue.create('Salvage Value', StandardOfValue.SALVAGE_VALUE),
    PresetValue.create('Scrap Value', StandardOfValue.SCRAP_VALUE),
    PresetValue.create('See Note', StandardOfValue.SEE_NOTE),
  ];

  repTypeValues: SearchableItem[] = [
    PresetValue.create('No Site Visit', ReportType.NO_SITE_VISIT),
    PresetValue.create('With Site Visit', ReportType.WITH_SITE_VISIT),
    PresetValue.create('Liquor License Appraisal', ReportType.LIQUOR_LICENSE),
    PresetValue.create('Inventory Appraisal', ReportType.INVENTORY_APPRAISAL)
  ];

  entityTypes: SearchableItem[] = Helpers.getEntityTypes();
  helperTexts: any[] = Helpers.getBusinessForms();

  confirmationModal: BsModalRef;
  changeStageModal: BsModalRef;
  delayModal: BsModalRef;
  modalRef: BsModalRef;
  linkReportModal: BsModalRef;

  showButton = false;
  isDirtyFlag = false;
  copied = false;

  assigneeModal: BsModalRef;

  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '25rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no'
  };

  htmlContent = '';
  maxlength = 250;
  dateEngagedSub: any;
  sovPresets: String[] = [];
  modeSub: any;
  currentMode: SystemType;

  constructor(
    public reportProvider: ReportProvider,
    public reportService: ReportService,
    public userProvider: UserProvider,
    public clientProvider: ClientProvider,
    public clientUserProvider: ClientUserProvider,
    private modalService: BsModalService,
    private stageService: StageService,
    private checkListProvider: ChecklistProvider,
    public reportEntityProvider: ReportEntityProvider,
    public tagProvider: TagProvider,
    public userService: UserService,
    private router: Router,
    private clipboardService: ClipboardService,
    private modeService: ModeService,
    private documentProvider: DocumentProvider,
    private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private logService : LogService
  ) {
  }

  ngOnInit(): void {
    this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.userSub = this.userService.user$.subscribe(user => this.currentUser = user);
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;

      if (this.currentReport) {
        /* if (this.currentReport.DATE_ANTICIPATED) {
          this.currentReport.DATE_ANTICIPATED = moment(this.currentReport.DATE_ANTICIPATED).format('MM/DD/YYYY');
        }

        if (this.currentReport.DATE_ENGAGED) {
          this.currentReport.DATE_ENGAGED = moment(this.currentReport.DATE_ENGAGED).format('MMM DD, YYYY');
          //this.engageDateChange(this.currentReport.DATE_ENGAGED);
        }

        if(this.currentReport.DATE_ENGAGED_LETTER){
          this.currentReport.DATE_ENGAGED_LETTER = moment(this.currentReport.DATE_ENGAGED_LETTER).format('MMM DD, YYYY');
        } */

        if (this.currentReport.PURPOSE)
          if (this.currentReport.PURPOSE.length > 0)
            this.findOrAddPurposeValue(this.currentReport.PURPOSE);

        this.isCollapsed.NOTES = !!(this.currentReport.COMMENTS || this.currentReport.NOTES);

        if (this.currentReport.LINKED_REPORTS?.length > 0)
          this.isCollapsed.LINKED_REPORT = true;

        /*   if (this.dueDateField)
          this.dueDateField.updateValues(this.currentReport.DATE_DUE); */

        this.confirmAssignee();
        this.assignCollector();
        let currentYear = this.currentReport?.DATE_ENGAGED? moment(this.currentReport.DATE_ENGAGED).year() : moment().year();
        this.modifyMomentHolidays(currentYear);
      }
    });
    this.stagesSub = this.reportService.reportStages$.subscribe(stages => this.stages = stages);
    this.stagesListSub = this.stageService.stages$.subscribe(states => this.stagesList = states);

    /* this.dateEngagedSub = this.reportService.updateDateEngaged.subscribe(() => {
      if (this.dueDateField)
        this.dueDateField.updateValues(this.currentReport.DATE_DUE);
    }); */

    this.reportLockSub = this.reportService.reportLock$.subscribe(value => this.isLocked = value);
  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
    if (this.stagesSub) this.stagesSub.unsubscribe();
    if (this.stagesListSub) this.stagesListSub.unsubscribe();
    if (this.dateEngagedSub) this.dateEngagedSub.unsubscribe();
    if (this.reportLockSub) this.reportLockSub.unsubscribe();
    if (this.modeSub) this.modeSub.unsubscribe();
    if (this.userSub) this.userSub.unsubscribe();
  }

  modifyMomentHolidays(currentYear){
    moment.modifyHolidays.set(['New Year\'s Day', 'Memorial Day', 'Independence Day', 'Labor Day', 'Thanksgiving Day', 'Christmas Day'])
      .add({
        'Day before Christmas': {
          date: '12/24',
          keywords_y: ['day after xmas'],
          keywords: ['after xmas']
        },
        'Presidents Day': {
          date: '02/17',
          keywords_y: ['Presidents Day'],
          keywords: ['Presidents Day']
        },
        'Day after Thanksgiving': {
            date: this.getDayAfterThanksGiving(currentYear),
            keywords_y: ['day after thanksgiving'],
            keywords: ['after thanksgiving']
        },
        'New Year\'s Eve': {
          date: '12/31',
          keywords_y: ['New Year Eve'],
          keywords: ['new year eve']
        },
      });
  }

  getDayAfterThanksGiving(year: any): string {
    // Start at the 1st of November
    const firstOfMonth = moment([year, 10]).startOf('month');
    // Find the first Thursday of November
    let firstThursday = firstOfMonth.day('Thursday');
    if (firstThursday.month() !== 10) {
      // If the first Thursday is not in November, add one week
      firstThursday.add(1, 'week');
    }
    // Calculate Thanksgiving: Add 3 weeks to the first Thursday
    const dayAfterThanksgiving = firstThursday.add(3, 'weeks').add(1, 'day').format('MM/DD');
    return dayAfterThanksgiving;
  }

  selectedUser(type: ReportUserType, value: SearchableSelection): void {
    this.reportProvider.setReportUser(this.currentReport.ID, type.toString(), value ? (<User>value.VALUE).ID : null)
      .subscribe(data => {
        if (data.success) {
          if (data.result)
            this.currentReport.getUsers()[type.toString()] = ReportUser.parse(data.result);
          else this.currentReport.getUsers()[type.toString()] = null;
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  selectedClient(value: SearchableSelection): void {
    this.currentReport.CLIENT = value ? value.VALUE : null;
    this.currentReport.CLIENT_USERS = new Array<ReportClientUser>();

    this.reportProvider.save(this.currentReport.ID, 'CLIENT', this.currentReport.CLIENT ? this.currentReport.CLIENT.ID : null)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  selectedClientUser(type: ReportClientType, value: SearchableSelection): void {
    this.reportProvider.setClientUser(this.currentReport.ID, type.toString(), value ? (<ClientUser>value.VALUE).ID : null)
      .subscribe(data => {
        if (data.success) {
          if (data.result)
            this.setClientUserByType(ReportClientUser.parse(data.result));
          else this.clearClientUserByType(type);
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  selectedClientUserOther(usr: ReportClientUser, value: SearchableSelection): void {
    this.reportProvider.setClientUserOther(this.currentReport.ID, usr.ID, value ? (<ClientUser>value.VALUE).ID : null)
      .subscribe(data => {
        if (data.success)
          usr.CLIENT_USER = ReportClientUser.parse(data.result).CLIENT_USER;
        else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  purposeChanged(value: SearchableSelection): void {
    const sValue = value != null ? value.VALUE : null;
    this.findOrAddPurposeValue(sValue);

    this.reportProvider.save(this.currentReport.ID, 'PURPOSE', this.currentReport.PURPOSE)
      .subscribe(data => {
        if (data.success) {
          if (this.currentReport.PURPOSE === 'Refinance' ||
            this.currentReport.PURPOSE === 'Collateralization') {
            this.currentReport.VALUATION_TYPE = ReportValuationType.STOCK;
          } else
            this.currentReport.VALUATION_TYPE = null;

          this.saveValuationType();
        }
      });
  }

  private findOrAddPurposeValue(value: string): void {
    let valueFound = false;

    if (value != null) {
      for (const p of this.purposeValues) {
        if (p.getValue() === value) {
          this.currentReport.PURPOSE = value;
          valueFound = true;
        }
      }

      if (!valueFound) {
        const pv = PresetValue.create(value, value);
        this.purposeValues.push(pv);
        this.currentReport.PURPOSE = value;
      }
    } else this.currentReport.PURPOSE = null;
  }

  valuationTypeChanged(value: SearchableSelection): void {
    this.currentReport.VALUATION_TYPE = value != null ? value.VALUE : null;
    this.setTurnFromAndTurnTo({FIELD: 'VALUATION_TYPE', VALUE: this.currentReport.VALUATION_TYPE});
  }


  setTurnFromAndTurnTo(field: any): void {
    if (this.currentReport.VALUATION_TYPE == ReportValuationType.SUMMARY && this.currentReport.REPORT_TYPE == ReportType.WITH_SITE_VISIT) {

      let fields: any[] = [
        { FIELD: 'TURN_FROM', VALUE: 10 },
        { FIELD: 'TURN_TO', VALUE: 12 }
      ];

      fields.push(field);

      this.reportProvider.saveFields(this.currentReport.ID, fields)
        .subscribe(data => {
          if (data.success){
            this.currentReport.TURN_FROM = 10;
            this.currentReport.TURN_TO = 12;
          }
          else
            this.logService.error(JSON.stringify(data, null, 2));
        }, (err) => {
          this.logService.error(JSON.stringify(err, null, 2));
        });
    }
    else {

      let fields: any[] = [
        { FIELD: 'TURN_FROM', VALUE: 6 },
        { FIELD: 'TURN_TO', VALUE: 7 }
      ];

      fields.push(field);
      this.reportProvider.saveFields(this.currentReport.ID, fields )
        .subscribe(data => {
          if (data.success){
            this.currentReport.TURN_FROM = 6;
            this.currentReport.TURN_TO = 7;
          }
          else
            this.logService.error(JSON.stringify(data, null, 2));
        }, (err) => {
          this.logService.error(JSON.stringify(err, null, 2));
        });
    }
  }

  saveValuationType(): void {
    this.reportProvider.save(this.currentReport.ID, 'VALUATION_TYPE', this.currentReport.VALUATION_TYPE)
      .subscribe(data => {
        if (data.success) {
          if (this.currentReport.CHECK_LIST) {
            if (this.currentReport.VALUATION_TYPE === ReportValuationType.STOCK)
              this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE = PurchaseAgreementType.STOCK;
            else if (this.currentReport.VALUATION_TYPE === ReportValuationType.ASSET)
              this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE = PurchaseAgreementType.ASSET;
            else if (this.currentReport.VALUATION_TYPE === ReportValuationType.TO_BE_DETERMINED)
              this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE = PurchaseAgreementType.TO_BE_DETERMINED;
            else this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE = null;

            // Update Checklist Field in Database
            this.checkListProvider.setField(this.currentReport.ID, 'PURCHASE_AGREEMENT_TYPE',
              this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE)
              .subscribe(data => {
                if (!data.success)
                  this.logService.error(JSON.stringify(data, null, 2));
              });
          } // If no Checklist is Setup, this way doesn't updateisMultipleChange
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  sofChanged(value: PresetValue[]): void {
    this.currentReport.STANDARD_OF_VALUES = value != null && value.length > 0 ? JSON.parse(JSON.stringify(value)): null;

    this.reportProvider.updateSov(this.currentReport.ID, this.currentReport.STANDARD_OF_VALUES)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  reportTypeChanged(value: SearchableSelection): void {
    this.currentReport.REPORT_TYPE = value != null ? value.VALUE : null;
    this.setTurnFromAndTurnTo({FIELD: 'REPORT_TYPE', VALUE: this.currentReport.REPORT_TYPE});
  }

  getClientUserByType(type: ReportClientType): ReportClientUser {
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS)
        if (cl.TYPE === type) {
          return cl;
        }

    }
    return null;
  }

  setClientUserByType(clientUser: ReportClientUser): void {
    if (this.currentReport.CLIENT_USERS) {
      for (let cl of this.currentReport.CLIENT_USERS) {
        if (cl.TYPE === clientUser.TYPE) {
          cl = clientUser;
          return;
        }
      }
      this.currentReport.CLIENT_USERS.push(clientUser);
    } else {
      this.currentReport.CLIENT_USERS = [];
      this.currentReport.CLIENT_USERS.push(clientUser);
    }
  }

  clearClientUserByType(type: ReportClientType): void {
    if (this.currentReport.CLIENT_USERS) {
      for (let i = 0; i < this.currentReport.CLIENT_USERS.length; i++)
        if (this.currentReport.CLIENT_USERS[i].TYPE === type)
          this.currentReport.CLIENT_USERS.splice(i, 1);
    }
  }

  setClientUserToggle(value: boolean, type: string, clientUser: ReportClientUser): void {
    this.reportProvider.setClientUserToggle(this.currentReport.ID, clientUser.ID, type, value)
      .subscribe(data => {
        if (data.success) {
          switch (type) {
          case 'EMAIL_CC':
            clientUser.EMAIL_CC = value;
            break;
          case 'EMAIL_ENGAGE':
            clientUser.EMAIL_ENGAGE = value;
            break;
          default:
            this.logService.error(`Unknown Type [${type}] Data Saved, but not Showing...`);
          }
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  addEntity(): void {
    this.reportEntityProvider.addEntity(this.currentReport.ID)
      .subscribe(data => {
        if (data.success) {
          if (!this.currentReport.ENTITIES)
            this.currentReport.ENTITIES = [];

          this.currentReport.ENTITIES.push(CorporateEntity.parse(data.result.ENTITY));

          if (this.currentReport.CHECK_LIST != null) {
            const details: CheckListEntityDetails = CheckListEntityDetails.parse(data.result.CHECKLIST_ENTITY_DETAILS);
            this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST.push(details);
          }
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  removeEntity(entity: CorporateEntity): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent);
    const ackSubject = new Subject<any>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Corporate Entity';
    this.confirmationModal.content.text =
      `Do you really want to remove <strong>${entity.NAME}</strong> from this Report?`;
    this.confirmationModal.content.actionButton = 'Remove';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportEntityProvider.deleteEntity(this.currentReport.ID, entity.ID)
          .subscribe(data => {
            if (data.success) {
              if (this.currentReport.CHECK_LIST != null) {
                const found: CheckListEntityDetails = this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST.find(details => details.ENTITY.ID === entity.ID);
                this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST.splice(this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST.indexOf(found), 1);
              }
              this.currentReport.ENTITIES.splice(this.currentReport.ENTITIES.indexOf(entity), 1);
              this.removeMultipleEntity();
            } else this.logService.error(JSON.stringify(data, null, 2));
          });
      }
    });
  }

  removeMultipleEntity(): void {
    if (this.currentReport.ENTITIES.length == 1) {
      this.currentReport.IS_MULTIPLE_ENTITY = false;
      this.reportProvider.save(this.currentReport.ID, 'IS_MULTIPLE_ENTITY', this.currentReport.IS_MULTIPLE_ENTITY)
        .subscribe(data => {
          if (!data.success)
            this.logService.error(JSON.stringify(data, null, 2));
        });
    }
  }

  isMultipleChange($event: any): void {
    if (this.isDisabled || !this.isActiveEdit())
      return;

    $event.preventDefault();

    if (this.currentReport.IS_MULTIPLE_ENTITY === true && this.currentReport.ENTITIES.length > 1) {
      this.confirmationModal = this.modalService.show(ConfirmationDialogComponent);
      const ackSubject = new Subject<any>();
      this.confirmationModal.content.subject = ackSubject;
      this.confirmationModal.content.title = 'Multiple Corporate Entities';
      this.confirmationModal.content.text =
        'Setting Report <strong>Not Multiple Entity</strong> will cause <br/>All Extra Corporate Entities removed, are you sure?';
      this.confirmationModal.content.actionButton = 'Remove';
      this.confirmationModal.content.actionButtonClass = 'warning';

      ackSubject.subscribe(value => {
        if (value) {
          this.reportEntityProvider.clearEntity(this.currentReport.ID)
            .subscribe(data => {
              if (data.success) {
                this.currentReport.ENTITIES = CorporateEntity.parseArray(data.result);
                this.currentReport.IS_MULTIPLE_ENTITY = !this.currentReport.IS_MULTIPLE_ENTITY;

                this.reportProvider.save(this.currentReport.ID, 'IS_MULTIPLE_ENTITY', this.currentReport.IS_MULTIPLE_ENTITY)
                  .subscribe(result => {
                    if (!result.success)
                      this.logService.error(JSON.stringify(result, null, 2));
                  });

              } else this.logService.error(JSON.stringify(data, null, 2));
            });
        }
      });
    } else {
      this.currentReport.IS_MULTIPLE_ENTITY = !this.currentReport.IS_MULTIPLE_ENTITY;
      this.reportProvider.save(this.currentReport.ID, 'IS_MULTIPLE_ENTITY', this.currentReport.IS_MULTIPLE_ENTITY)
        .subscribe(data => {
          if (data.success) {
            this.addEntity();
          }
        });
    }
  }

  addClientUser(): void {
    this.reportProvider.createClientUserOther(this.currentReport.ID)
      .subscribe(data => {
        if (data.success) {
          if (!this.currentReport.CLIENT_USERS)
            this.currentReport.CLIENT_USERS = [];
          this.currentReport.CLIENT_USERS.push(ReportClientUser.parse(data.result));
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  removeClientUser(user: ReportClientUser): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Client User';
    this.confirmationModal.content.text =
      `Do you really want to remove <strong>${user.getName()}</strong> from this Report?`;
    this.confirmationModal.content.actionButton = 'Remove';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.deleteClientUserOther(this.currentReport.ID, user.ID)
          .subscribe(data => {
            if (data.success) {
              this.currentReport.CLIENT_USERS.splice(this.currentReport.CLIENT_USERS.indexOf(user), 1);
            } else this.logService.error(JSON.stringify(data, null, 2));
          });
      }
    });
  }

  setClientUserOtherLabel(value: string, user: ReportClientUser): void {
    this.reportProvider.setClientUserOtherLabel(this.currentReport.ID, user.ID, encodeURI(value))
      .subscribe(data => {
        if (data.success) {
          user.LABEL = value;
        } else this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  engageDateChange(value: Date): void {
    this.reportProvider.save(this.currentReport.ID, 'DATE_ENGAGED', value ? moment(value).format('YYYY-MM-DD') : null)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  changeStage(): void {
    this.changeStageModal = this.modalService.show(ReportStageModal, Helpers.getModalConfig());
    const ackSubject = new Subject<HistoryStage>();
    this.changeStageModal.content.subject = ackSubject;
    this.changeStageModal.content.reportId = this.currentReport.ID;
    this.changeStageModal.content.nextStageId = this.stageService.getNextStageId(this.currentReport.STAGE.ID);
    this.changeStageModal.content.userList = this.currentReport.USERS;
    this.changeStageModal.content.currentReport = this.currentReport;
    this.changeStageModal.content.setReportUsers();
    this.changeStageModal.content.getReportUser();
    this.changeStageModal.content.setStageList();

    ackSubject.subscribe(value => {
      if (value) {
        this.reportService.addStageHistory(value);
        this.currentReport.STAGE = this.stageService.getStageById(value.SID);
        this.currentReport.ASSIGNEE = value.ASSIGNEE;
        this.currentReport.STATE = value.STATE;

        if (value.SID === 'EAED') {
          this.currentReport.DATE_ENGAGED = moment(value.DATE_ENGAGED).format('MMM DD, YYYY');
          this.reportService.updateDateEngaged.emit();
        }

        switch (value.SID) {
        case 'INDI':
        case 'QUOT':
        case 'ORDR':
        case 'FBRI':
        case 'ELST':
        case 'ENGD':
          if (this.currentReport.USERS[ReportUserType.PROCESSOR.toString()] == null) {
            const reportUser: ReportUser = new ReportUser();
            reportUser.USER = User.parse(value.ASSIGNEE);
            this.currentReport.getUsers()[ReportUserType.PROCESSOR.toString()] = reportUser;
          }
          break;
        case 'STRT':
          if (this.currentReport.USERS[ReportUserType.RESEARCH_ANALYST.toString()] == null) {
            const reportUser: ReportUser = new ReportUser();
            reportUser.USER = User.parse(value.ASSIGNEE);
            this.currentReport.getUsers()[ReportUserType.RESEARCH_ANALYST.toString()] = reportUser;
          }
          break;
        case 'SPST':
        case 'SPRE':
        case 'R1DR':
        case 'RDY2':
          if (this.currentReport.USERS[ReportUserType.FINANCIAL_ANALYST.toString()] == null) {
            const reportUser: ReportUser = new ReportUser();
            reportUser.USER = User.parse(value.ASSIGNEE);
            this.currentReport.getUsers()[ReportUserType.FINANCIAL_ANALYST.toString()] = reportUser;
          }
          break;
        case 'REVW':
        case 'REV2':
          if (this.currentReport.USERS[ReportUserType.SENIOR_ANALYST.toString()] == null) {
            const reportUser: ReportUser = new ReportUser();
            reportUser.USER = User.parse(value.ASSIGNEE);
            this.currentReport.getUsers()[ReportUserType.SENIOR_ANALYST.toString()] = reportUser;
          }
          break;
        case 'RDY1':
        case 'VLCK':
          // Senior Reviewer - Rohul Patel
          break;
        case 'FINR':
          // Senior Appraiser - Neal Patel
          break;
        }
      }
    });
  }

  checkIfIsDue(valueDate: Date): Boolean {
    const dueDate = new Date(valueDate);
    dueDate.setHours(20, 0, 0);
    const nowDate = new Date();
    return nowDate > dueDate;
  }

  get isDisabled(): boolean {
    return this.isLocked ? !!this.currentReport.DOCUMENTS.length || this.currentReport.STAGE.ORDER >= 4 : false;
  }

  isActiveEdit(): boolean {
    return this.currentReport.STATE.ID !== 'CCD';
  }

  showAllToggleButton(): boolean {
    let result = true;
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS) {
        if (cl.EMAIL_ENGAGE)
          result = false;
      }
    }
    return result;
  }

  isShowEngagedLetter(type: ReportClientType): boolean {
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS) {
        if (cl.TYPE === type && cl.EMAIL_ENGAGE) {
          return true;
        }
      }
    }

    return false;
  }

  onValueSaved(entity: CorporateEntity): void {
    if (entity.IS_DEFAULT) {
      if (this.currentReport.primaryEntityDba) {
        this.dbaValueSaved();
      }
    }
  }

  setNotesField(): void {
    this.reportProvider.save(this.currentReport.ID, 'NOTES', this.currentReport.NOTES)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      });
  }

  confirmAssignee(): void {
    if (this.currentUser && this.currentUser.hasPosition(ReportUserType.EQUIPMENT_PROCESSOR.toString())) {
      if (this.currentReport.IMPORTED && this.currentReport.STAGE.ID === 'ORDR' && this.currentReport.emptyProcessor) {
        this.assigneeModal = this.modalService.show(ConfirmAssigneeModal, Helpers.getModalConfig());
        const ackSubject = new Subject<Boolean>();
        this.assigneeModal.content.subject = ackSubject;
        ackSubject.subscribe(value => {
          if (value) {
            this.reportProvider
              .setReportUser(this.currentReport.ID, ReportUserType.EQUIPMENT_PROCESSOR.toString(), this.currentUser.ID)
              .subscribe(data => {
                if (data.success) {
                  this.currentReport.getUsers()[ReportUserType.EQUIPMENT_PROCESSOR.toString()] = ReportUser.parse(data.result);
                }
              });

          }
        });
      }
    }
  }

  assignCollector(): void {
    if (!this.currentReport.ignore_collector) {
      if (this.currentReport.IMPORTED && this.currentReport.STAGE.ID === 'ORDR' && this.currentReport.hasClientCollector()) {
        this.assigneeModal = this.modalService.show(AssignCollectorModal, Helpers.getModalConfig());
        const ackSubject = new Subject<Boolean>();
        this.assigneeModal.content.subject = ackSubject;
        this.assigneeModal.content.currentReport = this.currentReport;

        ackSubject.subscribe(value => {
          if (value) {
            if (this.currentReport.CLIENT_USERS) {
              for (const users of this.currentReport.CLIENT_USERS) {
                if (users.TYPE === ReportClientType.PROCESSOR ||
                  users.TYPE === ReportClientType.BDO ||
                  users.TYPE === ReportClientType.UNDERWRITER) {
                  users.CLIENT_USER.CLIENT = this.currentReport.CLIENT;
                }
              }
            }
          }
        });
      }
    }
  }

  isOverrideChange(): void {
    this.reportProvider.save(this.currentReport.ID, 'OVERRIDE', this.currentReport.OVERRIDE)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      });
  }

  isSynchronizeChange(): void {
    this.reportProvider.save(this.currentReport.ID, 'SYNCHRONIZE', this.currentReport.SYNCHRONIZE)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      });
  }

  isAddendumChange(): void {
    this.reportProvider.save(this.currentReport.ID, 'ADDENDUM', this.currentReport.ADDENDUM)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      });
  }

  setAddendumContent(): void {
    this.reportProvider.save(this.currentReport.ID, 'ADDENDUM_CONTENT', this.currentReport.ADDENDUM_CONTENT)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      });
  }

  private dbaValueSaved(): void {
    this.reportProvider.saveDbaNames(this.currentReport.ID)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  setAnticipatedDate(): void {
    this.reportProvider.save(this.currentReport.ID, 'DATE_ANTICIPATED',
      this.currentReport.DATE_ANTICIPATED ? moment(this.currentReport.DATE_ANTICIPATED).format('YYYY-MM-DD') : null)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  engageFile(): void {

    const factory = this.resolver.resolveComponentFactory(EngageReportFileModal);
    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    const ackSubject = new Subject<boolean>();
    this.tooltip.instance.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value === true) {
        this.refreshReport();
      }

      if (this.tooltip) {
        this.tooltip.destroy();
      }
    });
  }

  editReportDelay(delay: ReportDelay, start: boolean): void {
    this.delayModal = this.modalService.show(EditReportDelayModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.delayModal.content.subject = ackSubject;
    this.delayModal.content.currentReport = this.currentReport;
    this.delayModal.content.delay = delay;
    this.delayModal.content.isStart = start;

    ackSubject.subscribe(value => {
      if (value) {
        // TO DO : Success result
      }
    });
  }


  changeAssignee(): void {
    this.modalRef = this.modalService.show(ChangeAssigneeModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = this.currentReport;
    this.modalRef.content.selectedAssignee = this.currentReport.ASSIGNEE;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          this.currentReport.ASSIGNEE = value.USER;
        }
      }
    });
  }

  setEngagementDate(value: Date): void {
    this.reportProvider.saveEngagement(this.currentReport.ID, value ? moment(value).format('YYYY-MM-DD') : null, this.currentMode)
      .subscribe(data => {
        if (data.success) {
          const recalcResult = ReportRecalculatedResult.parse(data.result);
          this.currentReport.DATE_ENGAGED = moment(recalcResult.DATE_ENGAGED).format('MMMM DD, YYYY');
          this.currentReport.DATE_DUE = moment(recalcResult.DATE_DUE).format('MMM DD, YYYY');
        }
      });
  }

  setDueDate(value: Date): void {
    this.reportProvider.save(this.currentReport.ID, 'DATE_DUE', value ? moment(value).format('YYYY-MM-DD') : null)
      .subscribe(data => {
        if (data.success) {
          this.currentReport.DATE_DUE = moment(value).format('MMM DD, YYYY');
        }
      });
  }

  clearDateAnticipated(): void {
    if (this.isDisabled || !this.isActiveEdit())
      return;
    this.currentReport.DATE_ANTICIPATED= null;
    this.setAnticipatedDate();
  }

  canBeSkipped(): boolean {
    return !this.isDirtyFlag;
  }

  isFormSubmitted(): boolean {
    return false;
  }

  isFormValid(): boolean {
    return false;
  }

  showInvalidFields(): void {
  }

  submitForm(): Promise<boolean> {
    return undefined;
  }

  submitFormNext(): void {
  }

  onValueTouched(value: boolean): void {
    this.isDirtyFlag = value;
  }

  updateEmittedValue(updatedObj: any): void {
    if (updatedObj && updatedObj.NOTES)
      this.currentReport.CLIENT.NOTES = updatedObj.NOTES;

    if(this.currentReport.CLIENT.IS_BANK){
      if (!this.currentReport.CLIENT_USERS)
        this.currentReport.CLIENT_USERS = [];

      if(this.currentReport.CLIENT_USERS.length === 0 ){
        this.reportProvider.createClientUserClientOther(this.currentReport.ID)
          .subscribe(data => {
            if (data.success) {
              this.currentReport.CLIENT_USERS.push(ReportClientUser.parse(data.result));
            } else this.logService.error(JSON.stringify(data, null, 2));
          });
      }

    }
  }

  updateClientNotes(updatedObj: any): void {
    if (updatedObj && updatedObj.NOTES)
      this.currentReport.CLIENT.NOTES = updatedObj.NOTES;
  }

  /** When tag is removed */
  tagRemoved(tag: Tag): void{
    this.tagProvider.deleteReportTag(tag, this.currentReport.ID)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  /** When new tag is selected */
  tagSelected(tag: Tag): void{
    this.tagProvider.addReportTag(tag, this.currentReport.ID)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  /** When Copy File Location */
  copy(): void {
    this.clipboardService.copyFromContent(this.currentReport.FILE_LOCATION);
    this.copied = true;
    setTimeout(() => {
      this.copied = false;
    }, 3000);
  }

  setEffectiveDate(): void {
    this.reportProvider.save(this.currentReport.ID, 'DATE_EFFECTIVE',
      this.currentReport.DATE_EFFECTIVE ? moment(this.currentReport.DATE_EFFECTIVE).format('YYYY-MM-DD') : null)
      .subscribe(data => {
        if (!data.success)
          this.logService.error(JSON.stringify(data, null, 2));
      });
  }

  clearEffectiveDate(): void {
    this.currentReport.DATE_EFFECTIVE = null;
    this.setEffectiveDate();
  }

  getEntityName(entity: CorporateEntity): string {
    return this.currentReport.ENTITIES.find(listEntity => listEntity.ID === entity.ID).NAME;
  }

  showEngageManual(): void {
    // Prevent multiple modal openings
    if (this.tooltip || this.isEngageModalOpen) {
      return;
    }

    this.isEngageModalOpen = true;

    const factory = this.resolver.resolveComponentFactory(EngageReportManualModal);
    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    const ackSubject = new Subject<boolean>();
    this.tooltip.instance.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value === true) {
        this.refreshReport();
      }

      if (this.tooltip) {
        this.tooltip.destroy();
        this.tooltip = null; // Reset to allow reopening
        this.isEngageModalOpen = false; // Reset state
      }
    });
  }

  refreshReport(): void {
    this.reportProvider.getReportById(this.currentReport.ID)
      .subscribe(
        data => {
          if (data.success)
            this.reportService.setReport(Report.parse(data.result));
          else {
            this.logService.error(data);
          }
        }, err => {
          this.logService.error(err);
        });
  }

  showEngage(): void {
    const factory = this.resolver.resolveComponentFactory(EngageReportModal);
    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    const ackSubject = new Subject<boolean>();
    this.tooltip.instance.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value === true) {
        this.refreshReport();
      }

      if (this.tooltip) {
        this.tooltip.destroy();
      }
    });
  }

  openRequestModal(): void {

    const factory = this.resolver.resolveComponentFactory(RequestInfoModal);
    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    const ackSubject = new Subject<boolean>();
    this.tooltip.instance.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.currentReport.INITIAL_SENT = true;
      }

      if (this.tooltip) {
        this.tooltip.destroy();
      }
    });
  }

  onDownloadPdf(): void {
    this.isLoadingPreview = true;
    this.reportProvider.getEngagementLetterPreview(this.currentReport.ID, this.currentMode)
      .subscribe(response => {
        this.isLoadingPreview = false;
        const blob = new Blob([response], { type: 'application/pdf' });
        FileSaver.saveAs(
          blob, `Reliant Business Valuation - ${this.currentReport.primaryEntity()}.pdf`);

      }, error => {
        this.isLoadingPreview = false;
        this.logService.error(error);
      });
  }

  onEditClientUser(): void {
    this.editModal = this.modalService.show(CreateNewClientUserModal, Helpers.getModalConfig());
    const clUsrAckSubject = new Subject<boolean>();
    this.editModal.content.subject = clUsrAckSubject;
    this.editModal.content.clientUser = this.getClientUserEngagedUser().CLIENT_USER;
    this.editModal.content.manageFlag = true;
    this.editModal.content.isEdit = true;
    clUsrAckSubject.subscribe(value => {
      if (value) {
        this.logService.log(value);
      }
    });
  }

  private getClientUserEngagedUser(): ReportClientUser {
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS)
        if (cl.EMAIL_ENGAGE === true)
          return cl;
    }
    return null;
  }

  /**Create Follow Up */
  followUp(): void {
    this.modalRef = this.modalService.show(ReportFollowUpModal, Helpers.getModalConfig());
    const ackSubject = new Subject<ReportFollowUp>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        this.currentReport.FOLLOW_UPS.push(value);
      }
    });
  }

  /** Link Report */
  linkReport(): void {
    this.linkReportModal = this.modalService.show(ReportLinkComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<Report>();
    this.linkReportModal.content.subject = ackSubject;
    this.linkReportModal.content.report = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        if (!this.currentReport.LINKED_REPORTS)
          this.currentReport.LINKED_REPORTS = [];

        this.currentReport.LINKED_REPORTS.push(ReportLink.parse(value));
      }
    });
  }

  /** Unlink report */
  unlinkReport(report: ReportLink): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Unlink Report';
    this.confirmationModal.content.text = 'Do you really want to unlink this Report?';
    this.confirmationModal.content.actionButton = 'Unlink';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.unlinkReport(this.currentReport.ID, report.ID)
          .subscribe(data => {
            if (data.success) {
              this.currentReport.LINKED_REPORTS =
                this.currentReport.LINKED_REPORTS.filter((filter) => { if (report.ID != filter.ID) return filter; });
            }
          }, err => {
            this.logService.error(err);
          });
      }
    });
  }

  /** Go to report */
  goToReport(report: ReportLink): void {
    report.isLoading = true;

    this.reportProvider.getReportById(report.ID)
      .subscribe(data => {
        if (data.success) {
          report.isLoading = false;

          let dbReport: Report = Report.parse(data.result);

          if (dbReport.TYPE === SystemType.BV) {
            this.router.navigate(['/report', report.ID, 'general'],
              { state: { report: dbReport } });

            this.modeService.mode.next(SystemType.BV);
          }
          else if (dbReport.TYPE === SystemType.EA) {
            this.router.navigate(['/report-ea', report.ID, 'general'],
              { state: { report: dbReport } });

            this.modeService.mode.next(SystemType.EA);
          }
        }
      });
  }

  setResearched(): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Set Report Researched';
    this.confirmationModal.content.text = `Do you really want to set Report Researched?`;
    this.confirmationModal.content.actionButton = 'Confirm';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.save(this.currentReport.ID, 'R_RESEARCH', this.currentUser.ID)
          .subscribe(data => {
            if (data.success) {
              this.currentReport.R_RESEARCH = this.currentUser;
            }
          });
      }
    });
  }

  setSpreaded(): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.confirmationModal.content.subject = ackSubject;
    this.confirmationModal.content.title = 'Set Report Spreaded';
    this.confirmationModal.content.text = `Do you really want to set Report Spreaded?`;
    this.confirmationModal.content.actionButton = 'Confirm';
    this.confirmationModal.content.actionButtonClass = 'warning';

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.save(this.currentReport.ID, 'R_SPREAD', this.currentUser.ID)
          .subscribe(data => {
            if (data.success) {
              this.currentReport.R_SPREAD = this.currentUser;
            }
          });
      }
    });
  }

  changeResearched(): void {
    this.modalRef = this.modalService.show(ChangeResearchedSpreadedModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = this.currentReport;
    this.modalRef.content.field = "R_RESEARCH";
    this.modalRef.content.selectedUser = this.currentReport.R_RESEARCH;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          this.currentReport.R_RESEARCH = value.USER;
        }
      }
    });
  }

  changeSpreaded(): void {
    this.modalRef = this.modalService.show(ChangeResearchedSpreadedModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.modalRef.content.subject = ackSubject;
    this.modalRef.content.report = this.currentReport;
    this.modalRef.content.field = "R_SPREAD";
    this.modalRef.content.selectedUser = this.currentReport.R_SPREAD;
    ackSubject.subscribe(value => {
      if (value) {
        if (value.ACCEPT) {
          this.currentReport.R_SPREAD = value.USER;
        }
      }
    });
  }

  onBusinessFormValueChanged(value: any, entity: CorporateEntity): void {
    entity.TYPE = value != null ? value.VALUE : null;
    this.reportEntityProvider.save(entity.ID, 'TYPE', entity.TYPE)
      .subscribe(data => {
        if (!data.success)
          this.logService.log(data);
      });
  }

  /**
   * Download Signed Document.
   */
  downloadDocument(event: MouseEvent, doc: Document): void {
    event.stopImmediatePropagation();
    doc.disabled = true;
    this.documentProvider.downloadDocument(this.currentReport.ID, doc.DOCUMENT_ID)
      .subscribe(resp => {
        this.saveToFileSystem(resp, `Reliant Business Valuation - ${this.getNameByType(doc.DOCUMENT_TYPE)}.pdf`);
        doc.disabled = false;
      }, error => {
        doc.disabled = false;
        this.logService.log(error.message);
      });
  }

  downloadDocumentZip(event: MouseEvent, doc: Document): void {
    event.stopImmediatePropagation();
    doc.disabled = true;
    this.documentProvider.downloadDocumentArchive(this.currentReport.ID, doc.DOCUMENT_ID)
      .subscribe(resp => {
        this.saveToFileSystem(resp, `${this.getNameByType(doc.DOCUMENT_TYPE)} - ${this.currentReport.getName()}.zip`);
        doc.disabled = false;
      }, error => {
        doc.disabled = false;
        this.logService.log(error.message);
      });
  }

  voidDocument(doc: Document): void {
    this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();

    this.confirmationModal.content.title = 'Void Document';
    this.confirmationModal.content.text = 'Do you really want to void this Document?';
    this.confirmationModal.content.actionButton = 'Yes';
    this.confirmationModal.content.actionButtonClass = '';
    this.confirmationModal.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        doc.disabled = true;
        this.documentProvider.voidDocument(this.currentReport.ID, doc.DOCUMENT_ID)
          .subscribe(data => {
            if (data.success) {
              doc.DOCUMENT_STATE = DocumentState.voided;
              doc.disabled = false;
            }
          }, err => {
            doc.disabled = false;
            this.logService.error(err);
          });
      }
    });
  }

  private getNameByType(type: DocumentType): string {
    switch (type) {
      case DocumentType.ENGAGEMENT_LETTER:
        return 'Engagement Letter';
      default:
        return 'Document';
    }
  }

  /**
   * Save response to file system.
   * @param response
   * @param fileName
   */
  private saveToFileSystem(response, fileName: string): void {
    const blob = new Blob([response], {});
    saveAs(blob, fileName);
  }

  /**
   * Check if current stage is "Site Visit Scheduled" for EA system
   */
  isSiteVisitStage(): boolean {
    return this.currentReport?.STAGE?.ID === 'EASCH';
  }

  /**
   * Open site visit manual modal
   */
  scheduleSiteVisitEmail(): void {
    // Prevent multiple modal openings
    if (this.tooltip || this.isSiteVisitModalOpen) {
      return;
    }

    this.isSiteVisitModalOpen = true;

    const factory = this.resolver.resolveComponentFactory(SiteVisitManualModal);
    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    const ackSubject = new Subject<boolean>();
    this.tooltip.instance.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value === true) {
        this.refreshReport();
      }

      if (this.tooltip) {
        this.tooltip.destroy();
        this.tooltip = null; // Reset to allow reopening
        this.isSiteVisitModalOpen = false; // Reset state
      }
    });
  }
}


class CollapsedInfo {
  COMPANY: boolean;
  CLIENT: boolean;
  CHECKLIST: boolean;
  ENGAGEMENT: boolean;
  NOTES: boolean;
  USERS: boolean;

  SUMMARY: boolean;
  ENGAGE: boolean;
  STAGE: boolean;
  SITE_VISIT: boolean;
  DELAY: boolean;
  FOLLOW_UP: boolean;
  LINKED_REPORT: boolean;
  DOCUMENTS: boolean;

  static createByRole(): CollapsedInfo {
    const ci = new CollapsedInfo();

    ci.COMPANY = true;
    ci.CLIENT = true;
    ci.CHECKLIST = true;
    ci.ENGAGEMENT = true;
    ci.NOTES = false;
    ci.USERS = true;

    ci.SUMMARY = true;
    ci.ENGAGE = true;
    ci.STAGE = true;
    ci.SITE_VISIT = true;
    ci.DELAY = false;
    ci.FOLLOW_UP = false;
    ci.LINKED_REPORT = false;
    ci.DOCUMENTS = false;

    return ci;
  }
}
